/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// State
import { TSelectOption } from '@global-types/index';
import { TStore } from '@store/index';
import { StateCreator } from 'zustand';
import {
	TMeterPoint,
	TMeterPointQueryParams,
	TParsedMeterPoint,
} from '../types';

/* -------------------------------------------------------------------------- */
/*                                  Interface                                 */
/* -------------------------------------------------------------------------- */
export interface IMeterPointSlice {
	/* Pagination */
	meterPointsTotalPages: number;
	meterPointsPage: number;
	meterPointsTotal: number;
	meterPointsPageSize: number;

	/* Datatable */
	meterPointsSelectedRecords: TParsedMeterPoint[];
	meterPointsDefaultSelectedFields: TSelectOption[];
	meterPointsOpenConfirmationModalDelete: {
		open: boolean;
		params: {
			id?: string;
		};
	};
	meterPointsQueryParams: TMeterPointQueryParams;
	meterPointsSortStatus: Partial<TMeterPoint>;
	meterPointsSortParams: string;
	meterPointsSelectedRow: Partial<TParsedMeterPoint>;
	meterPointsLastAccessTokenCreated: string | null;
	apiTokenOrganization: string | null;
	openDrawerMeterPointForm: boolean;
	paramsFromProject: {
		project?: string;
		site?: string;
		producer?: string;
	};
	setParamsFromProject: (_paramsFromProject: {
		project?: string;
		site?: string;
		producer?: string;
	}) => void;

	setMeterPointsSelectedRow: (
		_meterPointsSelectedRow: Partial<TParsedMeterPoint>
	) => void;

	setMeterPointOrganization: (
		_organizationId: string | null
	) => void;
	setMeterPointsPage: (_meterPointsPage: number) => void;
	setMeterPointsSelectedRecords: (
		_meterPointsSelectedRecords: TParsedMeterPoint[]
	) => void;
	setMeterPointsOpenConfirmationModalDelete: (_meterPointsOpenConfirmationModalDelete: {
		open: boolean;
		params: {
			id?: string;
		};
	}) => void;
	setMeterPointsQueryParams: (
		_meterPointsQueryParams: TMeterPointQueryParams
	) => void;
	setMeterPointsSortStatus: (
		_meterPointsSortStatus: Partial<TMeterPoint>
	) => void;
	setMeterPointsSortParams: (
		_meterPointsSortParams: string
	) => void;

	setMeterPointsPageSize: (_pageSize: number) => void;
	setMeterPointsDefaultSelectedFields: (
		_meterPointsDefaultSelectedFields: TSelectOption[]
	) => void;
	setOpenDrawerMeterPointForm: (
		_openDrawerMeterPointForm: boolean
	) => void;
}

/* -------------------------------------------------------------------------- */
/*                                    Slice                                   */
/* -------------------------------------------------------------------------- */

const initialState = {
	/* Pagination */
	meterPointsTotal: 0,
	meterPointsPage: 1,
	meterPointsPageSize: 100,
	meterPointsTotalPages: 0,
	/**************/
	openDrawerMeterPointForm: false,
	meterPoints: [],
	meterPointsSelectedRecords: [],
	meterPointsDefaultSelectedFields: [],
	meterPointsQueryParams: {},
	meterPointsSelectedRow: {},
	meterPointsSortParams: '-createdAt',
	apiTokenOrganization: null,
	meterPointsSortStatus: {},
	meterPointsLastAccessTokenCreated: null,
	paramsFromProject: {},
	meterPointsOpenConfirmationModalDelete: {
		id: '',
		open: false,
		params: '',
	},
	meterPointsOpenConfirmationModalVerify: {
		open: false,
		params: '',
	},
	meterPointsOpenConfirmationModalMint: {
		open: false,
		params: '',
	},
};

export const createMeterPointsSlice: StateCreator<
	TStore,
	[],
	[],
	IMeterPointSlice
> = (set) => ({
	...initialState,
	setOpenDrawerMeterPointForm: (
		_openDrawerMeterPointForm: boolean
	) =>
		set({
			openDrawerMeterPointForm: _openDrawerMeterPointForm,
		}),
	setMeterPointsDefaultSelectedFields: (
		_meterPointsDefaultSelectedFields: TSelectOption[]
	) =>
		set({
			meterPointsDefaultSelectedFields:
				_meterPointsDefaultSelectedFields,
		}),
	setMeterPointsSelectedRow: (
		_meterPointsSelectedRow: Partial<TParsedMeterPoint>
	) =>
		set({
			meterPointsSelectedRow: _meterPointsSelectedRow,
		}),
	setMeterPointOrganization: (
		_organizationId: string | null
	) => set({ apiTokenOrganization: _organizationId }),
	setMeterPointsLastAccessTokenCreated: (
		_meterPointsLastAccessTokenCreated: string
	) =>
		set({
			meterPointsLastAccessTokenCreated:
				_meterPointsLastAccessTokenCreated,
		}),
	setParamsFromProject: (_paramsFromProject) =>
		set({ paramsFromProject: _paramsFromProject }),
	setMeterPointsSortParams: (_sortStatus: string) =>
		set({ meterPointsSortParams: _sortStatus }),
	setMeterPointsSortStatus: (
		_sortStatus: Partial<TMeterPoint>
	) => set({ meterPointsSortStatus: _sortStatus }),
	setMeterPointsQueryParams: (
		_queryParams: TMeterPointQueryParams
	) => set({ meterPointsQueryParams: _queryParams }),
	setMeterPointsTotal: (_total: number) =>
		set({ meterPointsTotal: _total }),
	setMeterPointsOpenConfirmationModalDelete:
		(_meterPointsOpenConfirmationModalDelete: {
			open: boolean;
			params: {
				id?: string;
			};
		}) =>
			set({
				meterPointsOpenConfirmationModalDelete:
					_meterPointsOpenConfirmationModalDelete,
			}),
	setMeterPointsPage: (_page: number) =>
		set({ meterPointsPage: _page }),
	setMeterPointsPageSize: (_pageSize: number) =>
		set({ meterPointsPageSize: _pageSize }),
	setMeterPointsTotalPages: (_totalPages: number) =>
		set({ meterPointsTotalPages: _totalPages }),
	setMeterPointsSelectedRecords: (
		_meterPointsSelectedRecords: TParsedMeterPoint[]
	) =>
		set({
			meterPointsSelectedRecords:
				_meterPointsSelectedRecords,
		}),
});
